/**
 * "Report a problem" for a forge — the Arche Builder's report dialog, for
 * Fias Forge. The report goes to the same support queue (source 'forge').
 *
 * The server keeps no forge transcript, so the page sends what it holds: the
 * conversation and tool events of this page session, plus the telemetry
 * snapshot. The server adds the forge's recent turns, test builds and cost.
 * The tool events are capped so a long session stays under the body limit.
 */
import { useState } from 'react';
import { CheckCircle2, Copy, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { getDebugState } from '@/lib/telemetry';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ForgeMessage, ForgeToolEvent } from '../types';

const MAX_DESCRIPTION_LENGTH = 5000;
/** Most recent messages and tool events sent; older ones add little and cost bytes. */
const MAX_REPORTED_MESSAGES = 200;
const MAX_REPORTED_TOOL_EVENTS = 600;
/** Mirrors the route's cap on one message's text. */
const MAX_MESSAGE_CHARS = 100_000;

const SCOPE_OPTIONS = [
  { value: '1', label: 'Last turn' },
  { value: '3', label: 'Last 3 turns' },
  { value: '5', label: 'Last 5 turns' },
  { value: '10', label: 'Last 10 turns' },
];

export interface ForgeReportProblemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  forgeId: string;
  messages: ForgeMessage[];
  /** Tool events of completed turns (keyed by message) and of the live turn. */
  toolEventHistory: Record<string, ForgeToolEvent[]>;
  pendingToolEvents: ForgeToolEvent[];
}

export function ForgeReportProblemDialog({
  open,
  onOpenChange,
  forgeId,
  messages,
  toolEventHistory,
  pendingToolEvents,
}: ForgeReportProblemDialogProps) {
  const [description, setDescription] = useState('');
  const [problemScope, setProblemScope] = useState('1');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportId, setReportId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setDescription('');
    setProblemScope('1');
    setError(null);
    setReportId(null);
    setCopied(false);
  };

  const close = () => {
    if (isSubmitting) return;
    reset();
    onOpenChange(false);
  };

  const submit = async () => {
    if (!description.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const toolEvents = [
        ...Object.entries(toolEventHistory).flatMap(([messageKey, events]) =>
          events.map((e) => ({ ...e, messageKey })),
        ),
        ...pendingToolEvents.map((e) => ({ ...e, messageKey: 'live' })),
      ].slice(-MAX_REPORTED_TOOL_EVENTS);
      const result = await api.submitForgeProblemReport(forgeId, {
        description: description.trim(),
        problemScope: Number(problemScope) as 1 | 3 | 5 | 10,
        conversationHistory: messages.slice(-MAX_REPORTED_MESSAGES).map((m) => ({
          role: m.role,
          content: m.content.slice(0, MAX_MESSAGE_CHARS),
          timestamp: m.timestamp,
        })),
        toolEvents,
        telemetryState: getDebugState(),
      });
      setReportId(result.reportId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The report could not be sent. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copy = async () => {
    if (!reportId) return;
    try {
      await navigator.clipboard.writeText(reportId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked: the id is selectable text.
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col">
        {reportId ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Report sent
              </DialogTitle>
              <DialogDescription>
                Thank you. Quote this id if you contact support about it.
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              <div className="flex items-center gap-2 py-4">
                <code className="flex-1 rounded bg-muted px-3 py-2 text-sm font-mono select-all">
                  {reportId}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void copy()}
                  aria-label="Copy report id"
                >
                  {copied ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </DialogBody>
            <DialogFooter>
              <Button onClick={close}>Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Report a problem</DialogTitle>
              <DialogDescription>
                Tell us what went wrong in this forge, and we will look into it.
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label htmlFor="forge-problem-scope" className="text-sm font-medium">
                    Where did the problem start?
                  </label>
                  <Select value={problemScope} onValueChange={setProblemScope}>
                    <SelectTrigger id="forge-problem-scope">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SCOPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label htmlFor="forge-problem-description" className="text-sm font-medium">
                    What went wrong?
                  </label>
                  <Textarea
                    id="forge-problem-description"
                    value={description}
                    onChange={(e) =>
                      setDescription(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))
                    }
                    placeholder="Describe what you expected versus what happened..."
                    rows={5}
                  />
                  <p className="text-xs text-muted-foreground text-right">
                    {description.length}/{MAX_DESCRIPTION_LENGTH}
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  The report includes this page&apos;s conversation and tool activity, and the
                  forge&apos;s recent turns, test builds and cost, to help diagnose the issue. No
                  personal data beyond your user ID is included.
                </p>
                {error && (
                  <p role="alert" className="text-sm text-destructive">
                    {error}
                  </p>
                )}
              </div>
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" onClick={close} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button onClick={() => void submit()} disabled={!description.trim() || isSubmitting}>
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Send report
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
